import { CardioOrResistance, Gym, GymTypes } from 'fitbeat.models';
import { DbGymRepository } from '../../persistence';
import { Exercise, ExerciseFormat } from '../exercise/exercise';
import { Zone } from '../gymschedule/zone';
import { UserWorkoutSession } from './userworkoutsession';
import { isStaticCardioZone, isStaticFunctionalZone } from '../../config';

// use a type enum instead of inheritance to make it easy to serialise
export enum ExerciseSessionType {
  Empty = 'Empty',
  User = 'User',
  Anonymous = 'Anonymous',
  Offline = 'Offline',
}

export class UserExerciseSession {
  public static setStation(exerciseSession: UserExerciseSession, stationNumber: number, gym: Gym) {
    exerciseSession.stationNumber = stationNumber;
    exerciseSession.zone = Zone.zoneForStation(stationNumber, gym);
  }

  public static shouldUseThirdAndFourthExercisesInStudio(
    session: UserExerciseSession,
  ) {
    return session.shouldUseAlternativeExercisesInStudio;
  }

  public static fromParentWorkoutSession(
    type: ExerciseSessionType,
    stationNumber: number,
    parentWorkoutSession: UserWorkoutSession,
    workoutStage: number,
    staticZoneNumber?: number,
  ): UserExerciseSession {
    const isCardioMachineZone = isStaticCardioZone(staticZoneNumber);
    const staticFunctionalZone = isStaticFunctionalZone(staticZoneNumber);
    const gym = DbGymRepository.getByName(parentWorkoutSession.gymName);
    const session = new UserExerciseSession(
      type,
      stationNumber,
      parentWorkoutSession,
      workoutStage,
      gym,
    );
    if (isCardioMachineZone) {
      session.format = ExerciseFormat.MachineTrain;
    }
    if(staticFunctionalZone) {
      session.format = ExerciseFormat.StaticFunctional;
    }
    return session;
  }

  public type: ExerciseSessionType;

  public isPause = false;

  public isWarmupOrWarmdown = false;

  public stationNumber: number; // 0 indexed

  public readonly parentWorkoutSession: UserWorkoutSession;

  public exercise: Exercise;

  public alternateExercise?: Exercise;

  public secondAlternateExercise?: Exercise;

  public thirdAlternateExercise?: Exercise;

  public format: ExerciseFormat = ExerciseFormat.Burn2Endurance;

  public readonly workoutStage: number;

  public zone: Zone;

  // when the amount of zones in a studio gym is less than amount of stages excluding warmups in a workout,
  // then the workout will loop around the gym again at the end
  // loop indicates in which loop this exercise occurred. 0 is the first loop
  // warmups don't count towards a loop
  public shouldUseAlternativeExercisesInStudio: boolean | undefined = undefined;

  constructor(
    type: ExerciseSessionType,
    stationNumber: number,
    parentWorkoutSession?: UserWorkoutSession,
    workoutStage = 1,
    // TODO: always pass in gym and do not default to test gym
    gym: Gym = DbGymRepository.getTestGym(),
  ) {
    // use !== and not instanceof, since the parameter might be a deserialized UserWorkoutSession
    if (parentWorkoutSession !== undefined) {
      this.parentWorkoutSession = parentWorkoutSession;
    }
    UserExerciseSession.setStation(this, stationNumber, gym);
    this.workoutStage = workoutStage;
    this.type = type;
    if (type === ExerciseSessionType.Empty && gym.type === GymTypes.Indoor) {
      this.format = ExerciseFormat.StudioEmpty;
    }
  }

  public setPause(pauseType: ExerciseFormat) {
    this.isPause = true;
    this.format = pauseType;
  }

  public setSessionExercise(exercise: Exercise) {
    this.exercise = exercise;
  }

  public setAlternativeSessionExercise(alternateExercise: Exercise) {
    this.alternateExercise = alternateExercise;
  }

  public exerciseInOrder(): Exercise[] {
    const exs = [this.exercise];
    if (this.alternateExercise) {
      exs.push(this.alternateExercise);
    }
    if (this.secondAlternateExercise) {
      exs.push(this.secondAlternateExercise);
    }
    if (this.thirdAlternateExercise) {
      exs.push(this.thirdAlternateExercise);
    }
    return exs;
  }

  public setExercisesInOrder(exerciseClones: Exercise[]) {
    if (exerciseClones[0]) {
      this.exercise = exerciseClones[0];
    }
    if (exerciseClones[1]) {
      this.alternateExercise = exerciseClones[1];
    }
    if (exerciseClones[2]) {
      this.secondAlternateExercise = exerciseClones[2];
    }
    if (exerciseClones[3]) {
      this.thirdAlternateExercise = exerciseClones[3];
    }
  }

  public isEmpty(): boolean {
    return this.type === ExerciseSessionType.Empty;
  }

  public getIndexForCurrentExercise() {
    let pauseAndWarmupCounter = 1; // starts at one because list is 0 indexed but workoutStage is not
    let populatedFirstAndSecondExercise = false;
    let fourExerciseChoiceSway = 0;
    for (let i = 0; i < this.workoutStage; i++) {
      const exerciseSessions = this.parentWorkoutSession.exerciseSessions;
      if (exerciseSessions[i].isPause || exerciseSessions[i].isWarmupOrWarmdown) {
        pauseAndWarmupCounter ++;
      } else {
        if (this.parentWorkoutSession.settings.cardioOrResistance === CardioOrResistance.Balance) {
          if (populatedFirstAndSecondExercise) {
            fourExerciseChoiceSway ++;
            populatedFirstAndSecondExercise = false;
          } else {
            populatedFirstAndSecondExercise = true;
          }
        }
      }
    }
    return this.workoutStage - (pauseAndWarmupCounter + fourExerciseChoiceSway);
  }
}
